import axios from "axios";
import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { getCookie } from "../../../../utils/cookieUtils";
import { errorNoti } from "../../../../base/BasicNotifications";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  ADMIN_ACCESS_TOKEN_COOKIE_KEY,
  ADMINS_COACH_ID_COOKIE_KEY,
} from "../../../../data/enums/TokenEnums";
import { annualOkrUrls } from "./AnnualOkrUrls";
import { MILESTONES } from "../AnnualOkrHelpers";

// ─────────────────────────────────────────────────────────────────────────────
// AnnualOkrStore — the coach side's Annual OKR data layer.
//
// Vanilla zustand + a hand-rolled selector hook, per CLAUDE.md and matching
// `ExecutionBlueprintStore.js`. Define selectors at module scope, never inline:
// the hook resubscribes on `selector` identity change, so an inline arrow
// resubscribes every render.
//
// Contract: `docs/annual-okr/20-fe-integration-guide.md` §6.
// ─────────────────────────────────────────────────────────────────────────────

const nodeInstance = axios.create({
  baseURL: process.env.REACT_APP_NEST_BASE_URL,
});

const useAnnualOkrStore = createStore((set, get) => ({
  clients: null,
  dashboardLoading: false,
  tracking: null,
  trackingLoading: false,

  // ── Auth ──────────────────────────────────────────────────────────────────
  // Copied verbatim from `ExecutionBlueprintStore._resolveAuthHeader`, as
  // CLAUDE.md instructs — access token first, else the admin-impersonating-a-
  // coach pair.
  //
  // ⚠️ The handoff §3 says every `/coach/...` route needs a `coach_id` header
  // (and `user_id` on write actions). This sends neither, deliberately: EXW's
  // coach routes sit behind the **same** `CoachAuthGuard` and work on the
  // Authorization header alone, with the guard resolving the coach from the
  // token. The one place this repo sends `coach_id` explicitly is the global
  // interceptor in `sharedRedux/sharedAction.js`, and only as a query param for
  // the admin-impersonation path.
  //
  // If a coach route 401/403s where the same call works for EXW, this is the
  // first thing to revisit — add the headers rather than changing the token
  // format, since the token format is the part that is already proven.
  _resolveAuthHeader: () => {
    const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
    const adminAccessToken = getCookie(ADMIN_ACCESS_TOKEN_COOKIE_KEY);
    const adminsCoachId = getCookie(ADMINS_COACH_ID_COOKIE_KEY);
    if (accessToken) return `Bearer ${accessToken}`;
    if (adminAccessToken && adminsCoachId) return `Bearer ${adminAccessToken}`;
    return null;
  },

  _get: (url) => {
    const authHeader = get()._resolveAuthHeader();
    return nodeInstance.get(url, {
      headers: authHeader ? { Authorization: authHeader } : {},
    });
  },

  // ── §6.1 GET /coach/annual-okr/dashboard ──────────────────────────────────
  // One card per client company. The payload is mapped to the shape
  // `AnnualOkr.js`'s ClientCard already expected, so the screen did not have to
  // change to accept real data.
  fetchDashboard: async () => {
    set({ dashboardLoading: true });
    try {
      const response = await get()._get(annualOkrUrls.GET_DASHBOARD);
      // §6.1 returns the array directly as `data`. The nested-`clients` branch
      // is defensive only — EXW's dashboard needed it, and paying one `??` here
      // is cheaper than an empty screen if this one ever grows a wrapper.
      const raw = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data?.data?.clients)
          ? response.data.data.clients
          : [];
      set({ clients: raw.map(mapDashboardClient), dashboardLoading: false });
    } catch (error) {
      errorNoti(
        error.response?.data?.message || "Annual OKR dashboard get failed",
      );
      set({ clients: [], dashboardLoading: false });
    }
  },
  // ── §6.9 GET /coach/annual-okr/:uniqueId/tracking ─────────────────────────
  // The locked board, read through the COACH's own tracking route (shipped
  // 2026-09-22). Same shape as the CEO's §4.6, so the payload goes STRAIGHT to
  // `OkrScorecardBoard` with no mapping at all.
  //
  // ⚠️ THE MAPPER THAT USED TO SIT HERE IS GONE, deliberately. Two earlier
  // routes needed one — see the long note on `GET_TRACKING` in
  // `AnnualOkrUrls.js` — and `mapOkrModeToTracking` existed to null-fill the
  // `progress_status` and `owner_name` that §6.6 didn't carry. Both arrive now,
  // so re-introducing a mapper would only be a place for the two apps' boards
  // to drift: they render the same component from the same shape.
  fetchTracking: async (uniqueId) => {
    if (!uniqueId) return null;
    set({ trackingLoading: true });
    try {
      const response = await get()._get(
        annualOkrUrls.GET_TRACKING.replace("{uniqueId}", uniqueId),
      );
      const data = response.data?.data || null;
      set({ tracking: data, trackingLoading: false });
      return data;
    } catch (error) {
      errorNoti(
        error.response?.data?.message || "Could not load the Annual OKRs",
      );
      set({ trackingLoading: false });
      return null;
    }
  },
}));

// ─── Payload → card props ───────────────────────────────────────────────────
// Kept as a named function rather than inlined so the mapping is testable and
// so the one place the server's field names appear is here.
//
// `annual_okr_unique_id` is **undefined** when no run exists yet (§6.1) — not
// null — which is why the card's CTA has to be gated on it rather than assuming
// an id is always present.
export const mapDashboardClient = (row) => ({
  // React key + accordion identity. `company_id` is the only field guaranteed
  // present on every row, including `NOT_STARTED` ones with no run.
  id: row.company_id,
  annualOkrId: row.annual_okr_unique_id || null,
  // Carried through so the card's CTA can route by it — see `goToOkrs`. The
  // card renders `stage_label` for the human copy and never this; the enum is
  // for routing only.
  stage: row.stage || null,
  name: row.company_name,
  ceo: row.ceo_name,
  initials: getInitials(row.company_name),
  // The server owns the milestone list and the position in it. Nothing is
  // derived locally — see the note on `timestamps` in `AnnualOkrSteps.js`.
  filled: row.stage_index ?? 0,
  timestamps: Array.isArray(row.timestamps) ? row.timestamps : [],
  // Our milestone wording, keyed by `stage`, over the API's `stage_label`
  // (e.g. "Coach reviewing Company OKRs") — the same call EXW's dashboard makes.
  // The API's label only shows for a stage this list doesn't know.
  statusText:
    MILESTONES.find((milestone) => milestone.key === row.stage)?.label ||
    (row.stage === "NOT_STARTED" ? "Not started" : row.stage_label || ""),
  // Only populated mid-interview (§6.1), so this is absent on most rows rather
  // than zero — hence the null check rather than a falsy one.
  statusSubText:
    row.objectives_confirmed != null && row.total_objectives != null
      ? `${row.objectives_confirmed} of ${row.total_objectives} objectives`
      : null,
  // At SENT_TO_COACH the button opens the review, so it says so — EXW's own
  // override of the backend label for the same stage (G32).
  buttonText:
    row.stage === "SENT_TO_COACH"
      ? "Start Review"
      : row.button?.label || "View OKRs",
  buttonEnabled: row.button?.enabled !== false,
});

// Initials for the avatar tile — first letters of the first two words, matching
// `ExecutionBlueprintStore`'s own helper.
const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() || "")
    .join("");

export const useAnnualOkrStoreSelector = (selector) => {
  const [selected, setSelected] = useState(() =>
    selector(useAnnualOkrStore.getState()),
  );
  useEffect(() => {
    const unsub = useAnnualOkrStore.subscribe((state) => {
      setSelected(() => selector(state));
    });
    return () => unsub();
  }, [selector]);
  return selected;
};

export default useAnnualOkrStore;
export { useAnnualOkrStore };
